import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity } from 'react-native';

import { useAppTheme } from '../../design-system/ThemeProvider';
import { t } from '../../i18n/translations';
import PrimaryButton from '../../shared/ui/PrimaryButton';
import { formatCount } from '../../utils/numberFormatter';
import { useLanguageStore } from '../quiz-core/languageStore';

const shellColors = {
  panel: 'rgba(8, 11, 36, 0.94)',
  panelHigh: 'rgba(255, 247, 218, 0.08)',
  gold: '#F8C84E',
  ivory: '#FFF7DA',
  muted: '#D9D2C2',
  green: '#153F27',
  line: 'rgba(248, 200, 78, 0.38)',
};

interface Member {
  user_id: string;
  user_profiles?: { name?: string; avatar_url?: string };
  role: string;
}

interface FamilyCardProps {
  family: { name: string; family_id?: string };
  members: Member[];
  userRole: string;
  onShareLink: (family: { name: string; family_id?: string }) => void;
  onShareCode: (family: { name: string; family_id?: string }) => void;
  onLeaveFamily: (family: { name: string; family_id?: string }) => void;
  onShowMembers: (family: { name: string; family_id?: string }) => void;
  onGoToLobby: (family: { name: string; family_id?: string }) => void;
}

const FamilyCard: React.FC<FamilyCardProps> = ({
  family,
  members,
  userRole,
  onShareLink,
  onShareCode,
  onLeaveFamily,
  onShowMembers,
  onGoToLobby,
}) => {
  const isAdmin = userRole === 'owner' || userRole === 'admin';
  const { language } = useLanguageStore();
  const { colors } = useAppTheme();
  const normalizedName = family.name?.trim().toLowerCase();
  const displayName = !normalizedName || normalizedName === 'new' || normalizedName === 'new family'
    ? `${t('familiesFriends.groupTypeFamily', language)} ${t('familiesFriends.group', language)}`
    : family.name;

  const getRoleText = (role: string) => {
    switch(role) {
      case 'owner': return t('familiesFriends.roleOwner', language);
      case 'admin': return t('familiesFriends.roleAdmin', language);
      case 'member': return t('familiesFriends.roleMember', language);
      default: return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  return (
    <View style={[premiumStyles.worldClassCard, { backgroundColor: shellColors.panel, borderColor: shellColors.line }]}>
      <View style={premiumStyles.headerSection}>
        <View style={premiumStyles.headerContent}>
          <View style={premiumStyles.titleRow}>
            <Text style={[premiumStyles.worldClassTitle, { color: shellColors.ivory }]} numberOfLines={2}>
              {displayName}
            </Text>
          </View>

          <View style={premiumStyles.metaRow}>
            {family.family_id && (
              <View style={premiumStyles.sophisticatedBadge}>
                <Text selectable style={premiumStyles.badgeCode}>{family.family_id}</Text>
              </View>
            )}
            <View style={[premiumStyles.statusPill, { backgroundColor: shellColors.panelHigh }]}>
              <View style={[premiumStyles.statusDot, { backgroundColor: colors.correct }]} />
              <Text style={[premiumStyles.statusPillText, { color: shellColors.ivory }]}>
                {t('familiesFriends.activeStatus', language)}
              </Text>
            </View>
          </View>

          <View style={premiumStyles.statsRow}>
            <View style={premiumStyles.statItem}>
              <Text style={[premiumStyles.statNumber, { color: shellColors.ivory }]}>{formatCount(members.length, language)}</Text>
              <Text style={[premiumStyles.statLabel, { color: shellColors.muted }]}>{t('familiesFriends.membersLabel', language)}</Text>
            </View>
            <View style={[premiumStyles.statDivider, { backgroundColor: shellColors.line }]} />
            <View style={premiumStyles.statItem}>
              <Ionicons
                name={userRole === 'owner' ? 'shield-checkmark' : userRole === 'admin' ? 'star' : 'person'}
                size={18}
                color={shellColors.gold}
                style={{ marginBottom: 4 }}
              />
              <Text style={[premiumStyles.statLabel, { color: shellColors.muted }]}>{getRoleText(userRole)}</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={premiumStyles.worldClassActionSection}>
        <PrimaryButton
          title={t('familiesFriends.enterFamilyLobby', language)}
          onPress={() => onGoToLobby(family)}
          style={premiumStyles.lobbyButton}
        />
        <View style={premiumStyles.secondaryActionRow}>
          {isAdmin && (
            <>
              <TouchableOpacity
                onPress={() => onShareLink(family)}
                style={[premiumStyles.secondaryActionChip, { backgroundColor: shellColors.panelHigh }]}
              >
                <Ionicons name="link-outline" size={14} color={shellColors.muted} />
                <Text style={[premiumStyles.secondaryActionText, { color: shellColors.muted }]} numberOfLines={1}>
                  {t('familiesFriends.shareLink', language).replace(/^[^\p{L}\p{N}]+/u, '').trim()}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => onShareCode(family)}
                style={[premiumStyles.secondaryActionChip, { backgroundColor: shellColors.panelHigh }]}
              >
                <Ionicons name="copy-outline" size={14} color={shellColors.muted} />
                <Text style={[premiumStyles.secondaryActionText, { color: shellColors.muted }]} numberOfLines={1}>
                  {t('familiesFriends.copyCode', language).replace(/^[^\p{L}\p{N}]+/u, '').trim()}
                </Text>
              </TouchableOpacity>
            </>
          )}
          <TouchableOpacity
            onPress={() => onShowMembers(family)}
            style={[premiumStyles.secondaryActionChip, { backgroundColor: shellColors.panelHigh }]}
          >
            <Ionicons name="people-outline" size={14} color={shellColors.muted} />
            <Text style={[premiumStyles.secondaryActionText, { color: shellColors.muted }]} numberOfLines={1}>
              {t('familiesFriends.viewMembers', language).replace(/^[^\p{L}\p{N}]+/u, '').trim()}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => onLeaveFamily(family)}
            style={[premiumStyles.secondaryActionChip, { backgroundColor: shellColors.panelHigh }]}
          >
            <Ionicons name="exit-outline" size={14} color={shellColors.muted} />
            <Text style={[premiumStyles.secondaryActionText, { color: shellColors.muted }]} numberOfLines={1}>
              {t('familiesFriends.leaveFamily', language).replace(/^[^\p{L}\p{N}]+/u, '').trim()}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

// Digital Sanctuary Design System Styles
const premiumStyles = StyleSheet.create({
  worldClassCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    marginBottom: 18,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    ...Platform.select({
      ios: {
        shadowColor: '#1D1A23',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0px 4px 16px rgba(29, 26, 35, 0.08)',
      },
    }),
  },
  headerSection: {
    padding: 20,
    paddingBottom: 16,
  },
  headerContent: {
    flex: 1,
  },
  titleRow: {
    marginBottom: 12,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    marginBottom: 14,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  premiumHeader: {
    marginBottom: 20,
  },
  titleSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  familyTitleContainer: {
    flex: 1,
    marginRight: 16,
  },
  worldClassTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1D1A23',
  },
  familySubtitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#777587',
    marginTop: 2,
    letterSpacing: 0.5,
  },
  sophisticatedBadge: {
    backgroundColor: shellColors.green,
    borderWidth: 1,
    borderColor: shellColors.line,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
    ...Platform.select({
      ios: {
        shadowColor: '#10B981',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 4,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0px 2px 8px 0px rgba(16, 185, 129, 0.3)',
      },
    }),
  },
  badgeLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.8)',
    letterSpacing: 1,
    marginBottom: 2,
  },
  badgeCode: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
    letterSpacing: 1.5,
  },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  statusPillText: {
    fontSize: 12,
    fontWeight: '700',
  },
  statsContainer: {
    flexDirection: 'row',
    backgroundColor: '#F3F2F8',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statItem: {
    alignItems: 'center',
    minWidth: 76,
  },
  statNumber: {
    fontSize: 20,
    fontWeight: '800',
    color: '#1D1A23',
    marginBottom: 4,
  },
  roleIndicator: {
    fontSize: 16,
    fontWeight: '700',
    color: '#4F46E5',
    marginBottom: 4,
    textTransform: 'capitalize',
  },
  statLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#777587',
    letterSpacing: 0.5,
    textAlign: 'center',
  },
  statDivider: {
    width: 1,
    height: 32,
    backgroundColor: '#E5E7EB',
    marginHorizontal: 16,
  },
  worldClassActionSection: {
    paddingHorizontal: 20,
    paddingBottom: 20,
    paddingTop: 4,
  },
  lobbyButton: {
    marginTop: 0,
  },
  secondaryActionRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 10,
  },
  secondaryActionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 999,
    paddingHorizontal: 9,
    paddingVertical: 7,
    gap: 6,
    opacity: 0.92,
  },
  secondaryActionText: {
    fontSize: 11,
    fontWeight: '600',
  },
});

export default FamilyCard;
